import React, { useCallback, useEffect, useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import { isBackgroundTrackAvailable, readBackgroundTrackLog, type BackgroundTrackReport } from '../backgroundTrack';

const clock = (ms: number) => (ms ? new Date(ms).toLocaleString('sv-SE', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '–');

/**
 * Felsökning av spåret i bakgrunden (iOS och Android): vad den inbyggda spåraren gjort — start/stopp
 * och varför, när appen gick i bakgrunden och kom fram, om systemet avslutade den, behörighet, antal
 * positioner och senaste sändning. Visas bara i apparna.
 */
export function BackgroundTrackLogSection() {
  const T = getTheme(useThemeMode().mode);
  const [report, setReport] = useState<BackgroundTrackReport | null>(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { void readBackgroundTrackLog().then(setReport); }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!isBackgroundTrackAvailable()) return null;

  const cS: React.CSSProperties = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, padding: '14px 16px', marginTop: 12 };
  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '2px 0' }}>
      <span style={{ color: T.textSub }}>{label}</span><span style={{ color: T.text, textAlign: 'right' }}>{value}</span>
    </div>
  );
  return (
    <div style={cS}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: T.textSub, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bakgrundsspår (felsökning)</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {open && <button onClick={load} style={{ background: 'none', border: 'none', color: T.accentText, fontSize: 12, cursor: 'pointer', padding: '2px 8px' }}>Uppdatera</button>}
          <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', color: T.accentText, fontSize: 12, cursor: 'pointer', padding: '2px 8px' }}>{open ? 'Dölj' : 'Visa'}</button>
        </div>
      </div>
      {open && !report && <div style={{ color: T.textSub, fontSize: 13, marginTop: 8 }}>Den här appversionen har ingen logg.</div>}
      {open && report && (
        <div style={{ marginTop: 10 }}>
          {row('Inspelning', report.running ? (report.hunt ? 'pågår (jakt)' : 'pågår (eget spår)') : 'av')}
          {row('Platsbehörighet', `${report.authorization}, ${report.accuracy}`)}
          {report.lowPower && row('Strömsparläge', 'på')}
          {row('Senaste position', clock(report.lastFix))}
          {row('Väntar på kartan / ej skickade', `${report.waiting} / ${report.unsent}`)}
          {row('Senaste sändning', `${clock(report.lastSend)} — ${report.lastSendResult}`)}
          <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto', fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', color: T.text }}>
            {report.events.length === 0 && <div style={{ color: T.textSub }}>Inga händelser ännu.</div>}
            {[...report.events].reverse().map((e, i) => (
              <div key={i} style={{ padding: '3px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ color: T.textSub }}>{clock(e.t)}</span> {e.text}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
